/**
 * ============================================================================
 * COLOR SYSTEM MODULE
 * ============================================================================
 *
 * Advanced color system with palette generation, accessibility checking,
 * and live preview capabilities.
 *
 * Features:
 * - Color palette generation (monochromatic, analogous, complementary, etc.)
 * - WCAG contrast compliance checking
 * - Color blindness simulation
 * - Live color updates
 * - Accessibility enhancements
 *
 * @author DeSight Studio GmbH
 * @version 1.0.0
 * ============================================================================
 */

class ColorSystem {
  constructor() {
    this.settings = window.CustomThemeSettings || {};
    this.colors = this.settings.colors || {};
    this.features = this.settings.features || {};

    this.contrastRatios = {
      AA_NORMAL: 4.5,
      AA_LARGE: 3,
      AAA_NORMAL: 7,
      AAA_LARGE: 4.5
    };

    this.init();
  }

  /**
   * Initialize the color system
   */
  init() {
    if (this.features.livePreview) {
      this.initLivePreview();
    }

    if (this.features.contrastChecker) {
      this.initContrastChecker();
    }

    if (this.features.colorPaletteGenerator !== 'disabled') {
      this.generateColorPalette(this.features.colorPaletteGenerator);
    }

    if (this.features.accessibilityMode) {
      this.enableAccessibilityMode();
    }

    this.bindEvents();
  }

  /**
   * Initialize live preview functionality
   */
  initLivePreview() {
    // Listen for Shopify theme editor changes
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', () => {
        this.updateColorsFromSettings();
      });

      document.addEventListener('shopify:section:unload', () => {
        this.updateColorsFromSettings();
      });
    }
  }

  /**
   * Update colors from current settings
   */
  updateColorsFromSettings() {
    const newSettings = window.CustomThemeSettings || {};
    const newColors = newSettings.colors || {};

    // Update CSS custom properties
    Object.entries(newColors).forEach(([key, value]) => {
      const cssVar = `--color-custom-${this.camelToKebab(key)}`;
      document.documentElement.style.setProperty(cssVar, value);
    });

    // Update internal colors object
    this.colors = newColors;

    // Emit color update event
    document.dispatchEvent(new CustomEvent('colorSystem:updated', {
      detail: { colors: newColors }
    }));
  }

  /**
   * Initialize contrast checker
   */
  initContrastChecker() {
    this.checkAllContrasts();

    // Check contrasts when colors change
    document.addEventListener('colorSystem:updated', () => {
      this.checkAllContrasts();
    });
  }

  /**
   * Check contrast ratios for all color combinations
   */
  checkAllContrasts() {
    const results = [];

    // Check primary combinations
    results.push(this.checkContrast(this.colors.primary, '#ffffff', 'Primary on White'));
    results.push(this.checkContrast(this.colors.primary, '#000000', 'Primary on Black'));

    // Check status colors
    results.push(this.checkContrast(this.colors.success, '#ffffff', 'Success on White'));
    results.push(this.checkContrast(this.colors.error, '#ffffff', 'Error on White'));
    results.push(this.checkContrast(this.colors.warning, '#000000', 'Warning on Black'));



    return results;
  }

  /**
   * Check contrast ratio between two colors
   */
  checkContrast(color1, color2, label = '') {
    const ratio = this.calculateContrastRatio(color1, color2);

    return {
      label,
      ratio,
      passes: {
        AA: ratio >= this.contrastRatios.AA_NORMAL,
        AAA: ratio >= this.contrastRatios.AAA_NORMAL,
        AA_LARGE: ratio >= this.contrastRatios.AA_LARGE,
        AAA_LARGE: ratio >= this.contrastRatios.AAA_LARGE
      }
    };
  }

  /**
   * Calculate contrast ratio between two colors
   */
  calculateContrastRatio(color1, color2) {
    const lum1 = this.getLuminance(color1);
    const lum2 = this.getLuminance(color2);

    const brightest = Math.max(lum1, lum2);
    const darkest = Math.min(lum1, lum2);

    return (brightest + 0.05) / (darkest + 0.05);
  }

  /**
   * Get relative luminance of a color
   */
  getLuminance(color) {
    const rgb = this.hexToRgb(color);
    if (!rgb) return 0;

    const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  /**
   * Generate color palette based on primary color
   */
  generateColorPalette(type) {
    const primaryHsl = this.hexToHsl(this.colors.primary);
    if (!primaryHsl) return;

    let palette = [];

    switch (type) {
      case 'monochromatic':
        palette = this.generateMonochromaticPalette(primaryHsl);
        break;
      case 'analogous':
        palette = this.generateAnalogousPalette(primaryHsl);
        break;
      case 'complementary':
        palette = this.generateComplementaryPalette(primaryHsl);
        break;
      case 'triadic':
        palette = this.generateTriadicPalette(primaryHsl);
        break;
      case 'tetradic':
        palette = this.generateTetradicPalette(primaryHsl);
        break;
    }

    // Apply generated palette
    this.applyGeneratedPalette(palette);

    return palette;
  }

  /**
   * Generate monochromatic color palette
   */
  generateMonochromaticPalette(hsl) {
    const { h, s, l } = hsl;

    return [
      this.hslToHex({ h, s, l: Math.max(0, l - 0.3) }),    // Darker
      this.hslToHex({ h, s, l: Math.max(0, l - 0.15) }),   // Dark
      this.hslToHex({ h, s, l }),                          // Original
      this.hslToHex({ h, s, l: Math.min(1, l + 0.15) }),   // Light
      this.hslToHex({ h, s, l: Math.min(1, l + 0.3) })     // Lighter
    ];
  }

  /**
   * Generate analogous color palette
   */
  generateAnalogousPalette(hsl) {
    const { h, s, l } = hsl;

    return [
      this.hslToHex({ h: (h - 60 + 360) % 360, s, l }),
      this.hslToHex({ h: (h - 30 + 360) % 360, s, l }),
      this.hslToHex({ h, s, l }),
      this.hslToHex({ h: (h + 30) % 360, s, l }),
      this.hslToHex({ h: (h + 60) % 360, s, l })
    ];
  }

  /**
   * Generate complementary color palette
   */
  generateComplementaryPalette(hsl) {
    const { h, s, l } = hsl;
    const complementary = (h + 180) % 360;

    return [
      this.hslToHex({ h, s, l }),
      this.hslToHex({ h: complementary, s, l }),
      this.hslToHex({ h, s: s * 0.7, l: l * 1.2 }),
      this.hslToHex({ h: complementary, s: s * 0.7, l: l * 1.2 }),
      this.hslToHex({ h, s: s * 0.5, l: l * 0.8 })
    ];
  }

  /**
   * Generate triadic color palette
   */
  generateTriadicPalette(hsl) {
    const { h, s, l } = hsl;

    return [
      this.hslToHex({ h, s, l }),
      this.hslToHex({ h: (h + 120) % 360, s, l }),
      this.hslToHex({ h: (h + 240) % 360, s, l }),
      this.hslToHex({ h, s: s * 0.7, l: l * 1.1 }),
      this.hslToHex({ h: (h + 120) % 360, s: s * 0.7, l: l * 1.1 })
    ];
  }

  /**
   * Generate tetradic color palette
   */
  generateTetradicPalette(hsl) {
    const { h, s, l } = hsl;

    return [
      this.hslToHex({ h, s, l }),
      this.hslToHex({ h: (h + 90) % 360, s, l }),
      this.hslToHex({ h: (h + 180) % 360, s, l }),
      this.hslToHex({ h: (h + 270) % 360, s, l }),
      this.hslToHex({ h, s: s * 0.6, l: l * 1.15 })
    ];
  }

  /**
   * Apply generated palette to theme
   */
  applyGeneratedPalette(palette) {
    if (palette.length >= 3) {
      // Update secondary and accent colors
      this.updateColor('secondary', palette[1]);
      this.updateColor('accent', palette[2]);

      // Update surface colors if available
      if (palette.length >= 5) {
        this.updateColor('surface', palette[3]);
        this.updateColor('neutralLight', palette[4]);
      }
    }
  }

  /**
   * Update a specific color
   */
  updateColor(colorName, hexValue) {
    const cssVar = `--color-custom-${this.camelToKebab(colorName)}`;
    document.documentElement.style.setProperty(cssVar, hexValue);

    // Update internal colors object
    this.colors[colorName] = hexValue;
  }

  /**
   * Enable accessibility mode
   */
  enableAccessibilityMode() {
    document.documentElement.classList.add('accessibility-mode');

    // Increase contrast for better visibility
    this.enhanceContrast();

    // Add focus indicators
    this.enhanceFocusIndicators();
  }

  /**
   * Enhance contrast for accessibility
   */
  enhanceContrast() {
    // Darken light colors and lighten dark colors
    const enhancedColors = {};

    Object.entries(this.colors).forEach(([key, value]) => {
      const hsl = this.hexToHsl(value);
      if (hsl) {
        // Increase saturation and adjust lightness for better contrast
        hsl.s = Math.min(1, hsl.s * 1.2);
        hsl.l = hsl.l > 0.5 ? Math.min(1, hsl.l * 1.1) : Math.max(0, hsl.l * 0.8);
        enhancedColors[key] = this.hslToHex(hsl);
      }
    });

    // Apply enhanced colors
    Object.entries(enhancedColors).forEach(([key, value]) => {
      this.updateColor(key, value);
    });
  }

  /**
   * Enhance focus indicators
   */
  enhanceFocusIndicators() {
    const style = document.createElement('style');
    style.textContent = `
      .accessibility-mode *:focus {
        outline: 3px solid var(--color-custom-primary) !important;
        outline-offset: 2px !important;
      }

      .accessibility-mode button:focus,
      .accessibility-mode a:focus,
      .accessibility-mode input:focus,
      .accessibility-mode select:focus,
      .accessibility-mode textarea:focus {
        box-shadow: 0 0 0 3px var(--color-custom-primary) !important;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Listen for theme editor changes
    document.addEventListener('custom-theme:settings-loaded', (e) => {
      this.settings = e.detail;
      this.colors = this.settings.colors || {};
      this.features = this.settings.features || {};
    });
  }

  // Utility methods

  /**
   * Convert camelCase to kebab-case
   */
  camelToKebab(str) {
    return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
  }

  /**
   * Convert hex to RGB
   */
  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  /**
   * Convert hex to HSL
   */
  hexToHsl(hex) {
    const rgb = this.hexToRgb(hex);
    if (!rgb) return null;

    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return { h: h * 360, s, l };
  }

  /**
   * Convert HSL to hex
   */
  hslToHex({ h, s, l }) {
    h = h / 360;

    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    let r, g, b;

    if (s === 0) {
      r = g = b = l;
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    const toHex = (c) => {
      const hex = Math.round(c * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
}

// Make ColorSystem available globally
window.ColorSystem = ColorSystem;
